import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectNaceCodes } from 'store/slices/dataSets/selectors';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import { useGetSingleMAInitiateQuery, useLazyGetSectorRiskAnalyzeListQuery } from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import {
  useDeleteSectorCustomSpecificTopicMutation,
  useGetSpecificCustomListQuery,
  useGetStateAISpecificRiskQuery,
  useRefreshAISpecificRiskMutation,
  useUpdateAISpecificRiskMutation,
} from 'store/slices/standards/apis/standardsApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';

import MaterialityConfigIndustryView from './MaterialityConfigIndustryView';

import type ISectorRiskAnalyzeItem from 'store/slices/materialAssessment/interfaces/ISectorRiskAnalyzeItem';
import type ISectorSpecificItem from 'store/slices/standards/interfaces/ISectorSpecificItem';

function MaterialityConfigIndustry() {
  const { recordId = '', vcmId = 0 } = useParams();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [valueToggle, setValueToggle] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<number>(-1);
  const [customOffset, setCustomOffset] = useState(0);
  const [customItems, setCustomItems] = useState<ISectorSpecificItem[]>([]);
  const [expandedRow, setExpandedRow] = useState<number>(-1);
  const [itemsAnalyzed, setItemsAnalyzed] = useState<ISectorRiskAnalyzeItem[]>([]);
  const [orderBy, setOrderBy] = useState<string[]>([]);

  const { data: userData } = useUserMeQuery();
  const { data: singleMA } = useGetSingleMAInitiateQuery({ id: recordId });

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const {
    data: stateAIData,
    isFetching: isStateAIFetching,
    refetch: refetchStateAI,
  } = useGetStateAISpecificRiskQuery({
    maId: recordId,
    vcmId: Number(vcmId),
  }, {
    skip: vcmId === 0,
  });
  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const { data: sectorSpecificCustom, isFetching: isSectorSpecificCustomLoading } = useGetSpecificCustomListQuery({
    offset: customOffset,
    maId: recordId,
    orderBy,
  });

  const [deleteSectorCustomSpecificTopic] = useDeleteSectorCustomSpecificTopicMutation();
  const [updateAISpecificRisk, { isLoading: isLoadingUpdate }] = useUpdateAISpecificRiskMutation();
  const [refreshAISpecificRisk, { isLoading: isLoadingRefresh }] = useRefreshAISpecificRiskMutation();
  const [updateSection] = useUpdateSectionMAMutation();

  const [lazyGetSectorRiskAnalyzeList] = useLazyGetSectorRiskAnalyzeListQuery();
  const { data: locationsCountries, isFetching: isLocationsCountriesLoading } = useGetLocationsCountriesQuery();

  const hasMoreCustom = sectorSpecificCustom?.data.meta?.hasNext || false;

  const onLoadMore = () => {
    if (hasMoreCustom) {
      setCustomOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  useEffect(() => {
    if (sectorSpecificCustom && sectorSpecificCustom?.data?.items.length > 0) {
      setCustomItems((prev) => {
        const newItems = sectorSpecificCustom?.data?.items.map((item) => ({ ...item, custom: true }));
        if (customOffset === 0) {
          return newItems;
        }
        const existingIds = new Set(prev.map((item) => item.id));
        const filteredNewItems = newItems.filter((item) => !existingIds.has(item.id));
        return [...prev, ...filteredNewItems];
      });
    }
  }, [sectorSpecificCustom, customOffset]);

  useEffect(() => {
    if (expandedRow !== -1) {
      lazyGetSectorRiskAnalyzeList({
        riskId: expandedRow,
        maId: recordId,
        offset: 0,
        vcmId: Number(vcmId),
      }).then((res) => {
        if ('data' in res && res.data) {
          setItemsAnalyzed(res.data.data.items);
        }
      });
    }
  }, [expandedRow, lazyGetSectorRiskAnalyzeList, recordId, vcmId]);

  const onCancel = () => {
    setOpenModal(false);
    setIsDeleteModalOpen(false);
    setSelectedItem(-1);
  };

  const onOpenModal = () => {
    setOpenModal(!openModal);
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const saveCompetitor = () => {
    if (!isViewOnly) {
      updateSection({
        maId: recordId,
        type: SECTION_TYPE.TOPIC_1_NACE_SECTION_RISKS,
        valueChainMapId: Number(vcmId),
        data: {
          isActive: true,
          status: customItems.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.EMPTY,
        },
      });
    }

    navigate(AppRoutes.materialityAssessmentTopicIdentification.replace(':recordId', recordId));
  };

  const onToggleRow = (id: number) => {
    setExpandedRow((prev) => (prev === id ? -1 : id));
  };

  const onChangeToggle = (value: boolean) => {
    setValueToggle(value);
  };

  const onDeleteItem = (id: number) => {
    setSelectedItem(id);
    setIsDeleteModalOpen(true);
  };

  const onDeleteConfirm = () => {
    setCustomItems((prev) => prev.filter((item) => item.id !== selectedItem));
    deleteSectorCustomSpecificTopic({ id: selectedItem, maId: recordId }).then(() => {
      toast.success(i18n.t('notifications.deletedSectorRisk'));
    }).catch(() => {
      toast.error(i18n.t('notifications.errorDeleteSectorRisk'));
    });
    onCancel();
  };

  const isAffectedDataOutdated = stateAIData?.data?.affectedUpToDate === false;
  const isGlobalRisksDataOutdated = stateAIData?.data?.globalRisksUpToDate === false;

  const onUpdate = () => {
    setCustomOffset(0);

    if (!valueToggle) {
      if (!isAffectedDataOutdated) {
        return;
      }

      refreshAISpecificRisk({ maId: recordId }).then(() => {
        toast.success(i18n.t('notifications.updatedSectorRisk'));
        refetchStateAI();
      }).catch(() => {
        toast.error(i18n.t('notifications.errorUpdateSectorRisk'));
      });
      return;
    }

    if (!isGlobalRisksDataOutdated) {
      return;
    }

    updateAISpecificRisk({ maId: recordId }).then(() => {
      toast.success(i18n.t('notifications.updatedSectorRisk'));
      refetchStateAI();
    }).catch(() => {
      toast.error(i18n.t('notifications.errorUpdateSectorRisk'));
    });
  };

  const onSortChange = (id: string) => {
    setOrderBy((prev) => {
      const hasId = prev.includes(id);
      const hasNegativeId = prev.includes(`-${id}`);
      if (hasNegativeId) {
        return prev.filter((item) => item !== `-${id}`);
      }
      if (hasId) {
        return prev.map((item) => (item === id ? `-${id}` : item));
      }
      return [id];
    });
    setCustomOffset(0);
  };

  const loading = isNaceCodesTreeLoading || isSectorSpecificCustomLoading
  || isLoadingUpdate || isStateAIFetching || isLoadingRefresh || isLocationsCountriesLoading;

  return (
    <MaterialityConfigIndustryView
      items={[...customItems]}
      isEmptyList={customItems.length === 0}
      loading={loading}
      onOpenModal={onOpenModal}
      goBack={goBack}
      saveCompetitor={saveCompetitor}
      openModal={openModal}
      hasMore={hasMoreCustom}
      onLoadMore={onLoadMore}
      valueToggle={valueToggle}
      onChangeToggle={onChangeToggle}
      onDeleteItem={onDeleteItem}
      naceCodesData={naceCodesData}
      onDeleteConfirm={onDeleteConfirm}
      isDeleteModalOpen={isDeleteModalOpen}
      onCancel={onCancel}
      expandedRow={expandedRow}
      setExpandedRow={onToggleRow}
      itemsAnalyzed={itemsAnalyzed}
      onUpdate={onUpdate}
      isAffectedDataOutdated={isAffectedDataOutdated}
      isGlobalRisksDataOutdated={isGlobalRisksDataOutdated}
      recordId={recordId}
      setCustomOffset={setCustomOffset}
      setOpenModal={setOpenModal}
      onSortChange={onSortChange}
      orderBy={orderBy}
      locationsCountries={locationsCountries?.data || []}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityConfigIndustry;
